import { useEffect, useMemo, useState } from 'react';
import { Bot, Plus, Trash2, Cpu, Wrench, Settings2, MessageSquare } from 'lucide-react';
import { useAgentStudioNavigate } from '../lib/routes';
import { PageHeader } from '../components/PageHeader';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { CardSkeletonGrid, EmptyState } from '../components/ui/States';
import { ConfirmDialog } from '../components/ui/Modal';
import { agentsService } from '../services/agentsService';
import { newAgent } from '../lib/factories';
import { TOOL_META } from '../lib/catalog';
import { toast } from '../store/uiStore';
import { useModelsStore } from '../store/modelsStore';
import type { Agent } from '../types';
import { AGENT_STUDIO_PERMISSIONS as P, useAgentStudioPermissions } from '../lib/permissions';

export function AgentsListPage() {
  const navigate = useAgentStudioNavigate();
  const { has } = useAgentStudioPermissions();
  const canManage = has(P.agentsManage);
  const canDelete = has(P.agentsDelete);
  const canChat = has(P.agentsChat);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [toDelete, setToDelete] = useState<Agent | null>(null);
  const labelOf = useModelsStore((s) => s.labelOf);

  useEffect(() => {
    agentsService.list().then((a) => {
      setAgents(a);
      setLoading(false);
    });
  }, []);

  const filtered = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return agents.filter((a) => !normalizedQuery || (a.name ?? '').toLowerCase().includes(normalizedQuery));
  }, [agents, query]);

  async function createAgent() {
    const agent = await agentsService.save(newAgent());
    navigate(`/agents/${agent.id}`);
  }

  async function confirmDelete() {
    if (!toDelete) return;
    await agentsService.remove(toDelete.id);
    setAgents((prev) => prev.filter((a) => a.id !== toDelete.id));
    toast.success(`Agente "${toDelete.name}" excluído.`);
  }

  return (
    <>
      <PageHeader
        title="Agentes"
        subtitle={`${agents.length} agentes no workspace`}
        search={query}
        onSearch={setQuery}
        searchPlaceholder="Buscar agente…"
        actions={canManage ? (
          <Button variant="primary" icon={Plus} onClick={createAgent}>
            Novo agente
          </Button>
        ) : undefined}
      />
      <div className="flex-1 overflow-y-auto scrollbar-thin p-8">
        {loading ? (
          <CardSkeletonGrid />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={Bot}
            title={query ? 'Nenhum agente encontrado' : 'Nenhum agente ainda'}
            description={query ? 'Tente outro termo de busca.' : 'Crie seu primeiro agente com persona, ferramentas e guardrails.'}
            action={!query && canManage ? <Button variant="primary" icon={Plus} onClick={createAgent}>Novo agente</Button> : undefined}
          />
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {filtered.map((agent) => (
              <Card
                key={agent.id || agent.name}
                className="group flex flex-col p-5 transition-all hover:shadow-soft"
              >
                <div className="flex items-start gap-3">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-orange text-white">
                    <Bot className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="truncate text-sm font-semibold text-ink">{agent.name}</h3>
                      {agent.status === 'active' ? (
                        <Badge tone="success" dot>Ativo</Badge>
                      ) : agent.status === 'draft' ? (
                        <Badge tone="warning" dot>Rascunho</Badge>
                      ) : (
                        <Badge tone="neutral">Arquivado</Badge>
                      )}
                    </div>
                    <p className="mt-0.5 line-clamp-2 text-xs text-slate">{agent.description || 'Sem descrição.'}</p>
                  </div>
                  {canDelete && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setToDelete(agent);
                      }}
                      className="rounded-lg p-1.5 text-slate opacity-0 transition-opacity hover:bg-danger/10 hover:text-danger group-hover:opacity-100"
                      aria-label="Excluir agente"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <Badge tone="navy"><Cpu className="h-3 w-3" /> {labelOf(agent.model)}</Badge>
                  <Badge tone="neutral"><Wrench className="h-3 w-3" /> {(agent.tools ?? []).length} ferramentas</Badge>
                </div>

                {(agent.tools ?? []).length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {(agent.tools ?? []).slice(0, 4).map((t) => (
                      <span key={t} className="rounded-md bg-cloud px-2 py-0.5 text-[11px] text-slate">
                        {TOOL_META[t]?.label ?? t}
                      </span>
                    ))}
                  </div>
                )}

                {(canManage || canChat) && (
                  <div className="mt-auto flex items-center gap-2 border-t border-line pt-4">
                    {canManage && <Button variant="secondary" size="sm" icon={Settings2} className="flex-1" onClick={() => navigate(`/agents/${agent.id}`)}>
                      Configurar
                    </Button>}
                    {canChat && <Button variant="primary" size="sm" icon={MessageSquare} className="flex-1" onClick={() => navigate(`/agents/${agent.id}/chat`)}>
                      Entrar no chat
                    </Button>}
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!toDelete}
        onClose={() => setToDelete(null)}
        onConfirm={confirmDelete}
        title="Excluir agente"
        message={`Tem certeza que deseja excluir "${toDelete?.name}"? Esta ação não pode ser desfeita.`}
      />
    </>
  );
}
